import { HELP_GUIDES } from "./guides/content";
import type { HelpTopic } from "./topics";

export interface HelpSearchResult {
  id: string;
  title: string;
  path: string;
  excerpt: string;
}

const normalize = (text: string) =>
  text
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export const searchHelp = (
  topics: HelpTopic[],
  query: string,
): HelpSearchResult[] => {
  const terms = normalize(query).trim().split(/\s+/).filter(Boolean);
  const matches: (HelpSearchResult & { score: number })[] = [];
  const visit = (nodes: HelpTopic[], parents: string[] = []) => {
    for (const topic of nodes) {
      const guide = HELP_GUIDES[topic.id];
      const passages = guide
        ? [
            guide.intro,
            guide.before,
            ...guide.sections.flatMap((section) => [
              `${section.title}. ${section.body}`,
              ...(section.callout
                ? [`${section.callout.title}. ${section.callout.body}`]
                : []),
            ]),
            ...(guide.task ? [guide.task] : []),
            ...(guide.previews?.map((preview) => preview.caption) ?? []),
            ...guide.checks,
            guide.problem.title,
            guide.problem.body,
          ]
        : [];
      const path = parents.join(" / ");
      const title = normalize(topic.title);
      const haystack = normalize([path, topic.title, ...passages].join(" "));
      if (!topic.isSection && terms.every((term) => haystack.includes(term))) {
        const excerpt =
          [...passages].sort(
            (a, b) =>
              terms.filter((term) => normalize(b).includes(term)).length -
              terms.filter((term) => normalize(a).includes(term)).length,
          )[0] ?? "";
        const score =
          terms.reduce(
            (sum, term) => sum + (title.includes(term) ? 10 : 1),
            0,
          ) + (title === normalize(query).trim() ? 30 : 0);
        matches.push({
          id: topic.id,
          title: topic.title,
          path,
          excerpt,
          score,
        });
      }
      visit(topic.children ?? [], [...parents, topic.title]);
    }
  };
  visit(topics);
  return matches.sort(
    (a, b) => b.score - a.score || a.title.localeCompare(b.title),
  );
};
